import React from 'react';
import { Card } from './ui/Card';
import { PlayerCareerStats } from './PlayerCareerStats';

function PlayerProfile({ player }) {
  // Function to render captain designation
  const renderCaptainDesignation = () => {
    if (player.captainStatus === 'captain') {
      return (
        <div className="mt-2 md:mt-0 md:ml-4 px-3 py-1 bg-yellow-600 text-white rounded-md font-bold text-sm flex items-center justify-center md:justify-start">
          Team Captain
        </div>
      );
    } else if (player.captainStatus === 'associate') {
      return (
        <div className="mt-2 md:mt-0 md:ml-4 px-3 py-1 bg-gray-700 text-white rounded-md font-bold text-sm flex items-center justify-center md:justify-start">
          Associate Captain
        </div>
      );
    }
    return null;
  };

  // Enhanced function to combine season stats and legacy stats, merging duplicates
  const getAllStats = () => {
    // Create a map to track seasons and combine duplicates
    const seasonStatsMap = new Map();
    const seasonGoalieMap = new Map();

    // Process season stats (newer format with season references and statsType)
    if (player.seasonStats && Array.isArray(player.seasonStats) && player.seasonStats.length > 0) {
      player.seasonStats.forEach((seasonStat) => {
        const seasonKey = seasonStat.seasonName || seasonStat.season?.name || 'Current Season';
        const statsType = seasonStat.statsType || 'skater';

        const statEntry = {
          season: seasonKey,
          team: 'Bombardiers',
          ...seasonStat,
          isSeasonStat: true,
        };

        if (statsType === 'goalie') {
          // Only include goalie stats if they have more than 1 game played
          if ((statEntry.gp || 0) > 1) {
            if (seasonGoalieMap.has(seasonKey)) {
              const existing = seasonGoalieMap.get(seasonKey);
              seasonGoalieMap.set(seasonKey, {
                ...existing,
                gp: (existing.gp || 0) + (statEntry.gp || 0),
                sa: (existing.sa || 0) + (statEntry.sa || 0),
                ga: (existing.ga || 0) + (statEntry.ga || 0),
                sv: (existing.sv || 0) + (statEntry.sv || 0),
                mins: (existing.mins || 0) + (statEntry.mins || 0),
                svp:
                  (existing.sa || 0) + (statEntry.sa || 0) > 0
                    ? (
                        (((existing.sv || 0) + (statEntry.sv || 0)) /
                          ((existing.sa || 0) + (statEntry.sa || 0))) *
                        100
                      ).toFixed(1)
                    : 0,
                gaa:
                  (existing.mins || 0) + (statEntry.mins || 0) > 0
                    ? (
                        (((existing.ga || 0) + (statEntry.ga || 0)) * 60) /
                        ((existing.mins || 0) + (statEntry.mins || 0))
                      ).toFixed(2)
                    : 0,
              });
            } else {
              seasonGoalieMap.set(seasonKey, statEntry);
            }
          }
        } else {
          // Only include skater stats if player can play skater positions
          // For pure goalies (positionGroup === 'Goalie' && !canPlaySkater), skip skater stats
          const isPureGoalie = player.positionGroup === 'Goalie' && !player.canPlaySkater;

          if (!isPureGoalie) {
            if (seasonStatsMap.has(seasonKey)) {
              const existing = seasonStatsMap.get(seasonKey);
              seasonStatsMap.set(seasonKey, {
                ...existing,
                gp: (existing.gp || 0) + (statEntry.gp || 0),
                g: (existing.g || 0) + (statEntry.g || 0),
                a: (existing.a || 0) + (statEntry.a || 0),
                pts:
                  (existing.g || 0) + (statEntry.g || 0) + ((existing.a || 0) + (statEntry.a || 0)),
                pim: (existing.pim || 0) + (statEntry.pim || 0),
                ppg: (existing.ppg || 0) + (statEntry.ppg || 0),
                ppa: (existing.ppa || 0) + (statEntry.ppa || 0),
                shg: (existing.shg || 0) + (statEntry.shg || 0),
                otg: (existing.otg || 0) + (statEntry.otg || 0),
                eng: (existing.eng || 0) + (statEntry.eng || 0),
                mins: (existing.mins || 0) + (statEntry.mins || 0),
              });
            } else {
              seasonStatsMap.set(seasonKey, statEntry);
            }
          }
        }
      });
    }

    // Add legacy stats (older format with string season names)
    if (player.stats && Array.isArray(player.stats) && player.stats.length > 0) {
      player.stats.forEach((legacyStat) => {
        const seasonKey = legacyStat.season;
        const statEntry = {
          ...legacyStat,
          isSeasonStat: false,
        };

        // Check if this looks like goalie stats and has more than 1 game played
        if (
          (legacyStat.sa > 0 || legacyStat.ga > 0 || legacyStat.sv > 0) &&
          (statEntry.gp || 0) > 1
        ) {
          if (seasonGoalieMap.has(seasonKey)) {
            const existing = seasonGoalieMap.get(seasonKey);
            seasonGoalieMap.set(seasonKey, {
              ...existing,
              gp: (existing.gp || 0) + (statEntry.gp || 0),
              sa: (existing.sa || 0) + (statEntry.sa || 0),
              ga: (existing.ga || 0) + (statEntry.ga || 0),
              sv: (existing.sv || 0) + (statEntry.sv || 0),
              mins: (existing.mins || 0) + (statEntry.mins || 0),
              svp:
                (existing.sa || 0) + (statEntry.sa || 0) > 0
                  ? (
                      (((existing.sv || 0) + (statEntry.sv || 0)) /
                        ((existing.sa || 0) + (statEntry.sa || 0))) *
                      100
                    ).toFixed(1)
                  : 0,
              gaa:
                (existing.mins || 0) + (statEntry.mins || 0) > 0
                  ? (
                      (((existing.ga || 0) + (statEntry.ga || 0)) * 60) /
                      ((existing.mins || 0) + (statEntry.mins || 0))
                    ).toFixed(2)
                  : 0,
            });
          } else {
            seasonGoalieMap.set(seasonKey, statEntry);
          }
        } else {
          // Only include skater stats if player can play skater positions
          // For pure goalies (positionGroup === 'Goalie' && !canPlaySkater), skip skater stats
          const isPureGoalie = player.positionGroup === 'Goalie' && !player.canPlaySkater;

          if (!isPureGoalie) {
            if (seasonStatsMap.has(seasonKey)) {
              const existing = seasonStatsMap.get(seasonKey);
              seasonStatsMap.set(seasonKey, {
                ...existing,
                gp: (existing.gp || 0) + (statEntry.gp || 0),
                g: (existing.g || 0) + (statEntry.g || 0),
                a: (existing.a || 0) + (statEntry.a || 0),
                pts:
                  (existing.g || 0) + (statEntry.g || 0) + ((existing.a || 0) + (statEntry.a || 0)),
                pim: (existing.pim || 0) + (statEntry.pim || 0),
                ppg: (existing.ppg || 0) + (statEntry.ppg || 0),
                ppa: (existing.ppa || 0) + (statEntry.ppa || 0),
                shg: (existing.shg || 0) + (statEntry.shg || 0),
                otg: (existing.otg || 0) + (statEntry.otg || 0),
                eng: (existing.eng || 0) + (statEntry.eng || 0),
                mins: (existing.mins || 0) + (statEntry.mins || 0),
              });
            } else {
              seasonStatsMap.set(seasonKey, statEntry);
            }
          }
        }
      });
    }

    // Convert maps back to arrays and sort by season (most recent first)
    const combinedSkaterStats = Array.from(seasonStatsMap.values()).sort((a, b) => {
      return b.season.localeCompare(a.season);
    });

    const combinedGoalieStats = Array.from(seasonGoalieMap.values()).sort((a, b) => {
      return b.season.localeCompare(a.season);
    });

    return {
      skaterStats: combinedSkaterStats,
      goalieStats: combinedGoalieStats,
    };
  };

  const stats = getAllStats();

  // Check if player can play multiple positions
  const canPlayMultiplePositions = player.canPlayGoalie || player.canPlaySkater;
  const primaryPosition = player.positionGroup;

  return (
    <div className="bg-black min-h-screen">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
          <div className="flex flex-col items-center md:flex-row md:items-start gap-6 md:gap-8">
            {/* Player Image */}
            <div className="w-48 h-48 md:w-64 md:h-64 bg-gray-800 rounded-lg overflow-hidden flex items-center justify-center relative flex-shrink-0">
              {/* Captain badge */}
              {player.captainStatus === 'captain' && (
                <div className="absolute top-2 right-2 bg-yellow-600 text-white text-sm font-bold px-2 py-1 rounded shadow-md z-10">
                  C
                </div>
              )}
              {player.captainStatus === 'associate' && (
                <div className="absolute top-2 right-2 bg-gray-700 text-white text-sm font-bold px-2 py-1 rounded shadow-md z-10">
                  A
                </div>
              )}

              <img
                src={player.imageUrl || '/player-placeholder.png'}
                alt={player.name}
                className="w-full h-full object-contain bg-gray-800"
                onError={(e) => {
                  e.currentTarget.src = '/player-placeholder.png';
                  e.currentTarget.classList.remove('object-cover');
                  e.currentTarget.classList.add('object-contain', 'p-6');
                }}
              />
            </div>

            {/* Player Info */}
            <div className="flex-1 text-center md:text-left min-w-0">
              {/* Name and Number */}
              <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 mb-4">
                <span className="text-4xl md:text-5xl font-bold text-yellow-500">
                  #{player.number}
                </span>
                <h1 className="text-2xl md:text-4xl font-bold text-white leading-tight">
                  {player.name}
                </h1>
              </div>

              {/* Captain Designation */}
              {renderCaptainDesignation()}

              {/* Dual Position Indicator */}
              {canPlayMultiplePositions && (
                <div className="mt-2 flex flex-wrap gap-2 justify-center md:justify-start">
                  <div className="px-3 py-1 bg-blue-700 text-white rounded-md text-sm">
                    Primary: {player.position}
                  </div>
                  {player.canPlayGoalie && primaryPosition !== 'Goalie' && (
                    <div className="px-3 py-1 bg-purple-700 text-white rounded-md text-sm">
                      Can Play Goalie
                    </div>
                  )}
                  {player.canPlaySkater && primaryPosition === 'Goalie' && (
                    <div className="px-3 py-1 bg-green-700 text-white rounded-md text-sm">
                      Can Play Skater
                    </div>
                  )}
                </div>
              )}

              {/* Player Details Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 text-gray-400 mt-6">
                <div className="text-center md:text-left">
                  <div className="text-sm">Position</div>
                  <div className="text-lg md:text-xl text-white">{player.position}</div>
                </div>
                <div className="text-center md:text-left">
                  <div className="text-sm">Height</div>
                  <div className="text-lg md:text-xl text-white">{player.height || 'N/A'}</div>
                </div>
                <div className="text-center md:text-left">
                  <div className="text-sm">Weight</div>
                  <div className="text-lg md:text-xl text-white">{player.weight || 'N/A'}</div>
                </div>
                <div className="text-center md:text-left">
                  <div className="text-sm">Born</div>
                  <div className="text-lg md:text-xl text-white">{player.birthdate || 'N/A'}</div>
                </div>
              </div>

              {/* Hometown - Full Width */}
              <div className="mt-4 text-center md:text-left">
                <div className="text-sm text-gray-400">Hometown</div>
                <div className="text-lg md:text-xl text-white">{player.hometown || 'N/A'}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <PlayerCareerStats
          skaterStats={stats.skaterStats}
          goalieStats={stats.goalieStats}
          showTables={true}
        />
      </div>

      {/* Bio Section */}
      {player.bio && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
          <h2 className="text-2xl font-bold text-white mb-6 text-center md:text-left">
            About {player.name}
          </h2>
          <Card variant="glass" className="bg-gray-900">
            <div className="p-6">
              <p className="text-gray-300 leading-relaxed">{player.bio}</p>
            </div>
          </Card>
        </div>
      )}

      {/* Career Highlights */}
      {player.highlights && player.highlights.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
          <h2 className="text-2xl font-bold text-white mb-6 text-center md:text-left">
            Career Highlights
          </h2>
          <Card variant="glass" className="bg-gray-900">
            <div className="p-6">
              <ul className="list-disc pl-6 space-y-3 text-gray-300">
                {player.highlights.map((highlight, index) => (
                  <li key={index}>{highlight}</li>
                ))}
              </ul>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

export default PlayerProfile;
